import {print as astToString} from 'query-api-graphql';
import React, {useCallback, useMemo, useState} from 'react';

import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import StepConfigurationViewTemplate from 'analysis-tool/ui/view-templates/StepConfigurationViewTemplate';
import {assert} from 'Asserts';
import {sum} from 'BenchlingUtil';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';
import countSelectedFields from 'query-api/graphql-field-selector/countSelectedFields';
import useQueryApiIntrospection from 'query-api/useQueryApiIntrospection';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {AsyncStatus, getMostRecentData, NOT_FETCHED_ASYNC_STATE} from 'ui-library/hooks/useAsyncState';
import useThrottle from 'ui-library/hooks/useThrottle';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import ChooseDifferentDataFrameCreationMethodButton from '../ChooseDifferentDataFrameCreationMethodButton';
import {
  selectedColumnsToColumnMappings,
  unselectColumnsIfColumnIsNotInTemplateColumns,
  getTemplateDataFrameColumns,
} from '../column-mapping/ColumnMappingUtil';
import {DATA_FRAME_CREATION_NO_PREVIEW_PLACEHOLDER_TEXT} from '../DataFrameCreationConstants';
import {DataFrameNameState} from '../DataFrameCreationTypes';
import {getSharedDataFrameCreationMethodProps, openSelectedItemForNewStep} from '../DataFrameCreationUtils';
import {useAutoUpdatePreview} from '../hooks/useAutoUpdatePreview';
import buildQueryApiQueryAst from './buildQueryApiQueryAst';
import formStateToHumanReadableString from './formStateToHumanReadableString';
import {FormState} from './RegistrySchemaDataFrameCreationTypes';
import RegistrySchemaStepperForm from './RegistrySchemasStepperForm';
import {FORM_STATE_THROTTLE_DURATION, MAX_NUM_QUERY_API_FIELDS} from './SchemaDatasetCreationConstants';
import {
  buildErrorAndWarningMessages,
  getPreviewFnForDatasetFromQueryApi,
  PreviewState,
} from './SchemaDatasetCreationUtils';

// TODO(BNCH-53555): Handle and test dataset name editing once design conversations have resolved (see ticket
// for details/links)
const DEFAULT_DATA_FRAME_NAME_STATE = {
  name: '',
  hasUserEdits: false,
};
const dataFrameNameGlobalState = makeUseGlobalClientState<DataFrameNameState>(DEFAULT_DATA_FRAME_NAME_STATE);

const DEFAULT_QUERY_FORM_STATE = {entityListingFieldName: null};
const registrySchemaFormGlobalState = makeUseGlobalClientState<FormState>(DEFAULT_QUERY_FORM_STATE);

const previewGlobalState = makeUseGlobalClientState<PreviewState>(NOT_FETCHED_ASYNC_STATE);

const isFinalizingGlobalState = makeUseGlobalClientState<boolean>(false);

export default function RegistrySchemasDataFrameCreationView({
  analysis,
  clientStateId,
  onOpenStepInitializationView,
  onCloseStepInitializationView,
  selectedItem,
}: MainViewComponentProps<AnalysisMainViewItemType.REGISTRY_SCHEMA_DATASET_CONFIGURATION>): JSX.Element {
  const [dataFrameNameState, setDataFrameNameState] =
    dataFrameNameGlobalState.useGlobalClientState(clientStateId);

  const [queryFormState, setQueryFormState] =
    registrySchemaFormGlobalState.useGlobalClientState(clientStateId);

  const [previewState, setPreviewState] = previewGlobalState.useGlobalClientState(clientStateId);

  const templateDataFrameColumns = useMemo(
    () => getTemplateDataFrameColumns(analysis, selectedItem.stepIdToReplace),
    [analysis, selectedItem.stepIdToReplace]
  );
  const [selectedColumns, setSelectedColumns] = useState<Array<DataFrameColumnDef | null>>([]);
  const [isManuallyChanged, setIsManuallyChanged] = useState<boolean>(false);

  const [isFinalizing, setIsFinalizing] = isFinalizingGlobalState.useGlobalClientState(clientStateId);

  const queryApiIntrospection = useQueryApiIntrospection();

  const queryAst = useMemo(() => buildQueryApiQueryAst(queryFormState), [queryFormState]);
  const throttledQueryAst = useThrottle(queryAst, FORM_STATE_THROTTLE_DURATION);

  const canFinalize =
    queryApiIntrospection.status === QueryResultType.SUCCESS &&
    queryAst != null &&
    queryApiIntrospection.getQueryAstValidationErrors(queryAst).length === 0 &&
    queryFormState.entityListingFieldName != null &&
    dataFrameNameState.name !== '';

  useAutoUpdatePreview(
    throttledQueryAst == null ? null : {queryAst: throttledQueryAst},
    getPreviewFnForDatasetFromQueryApi(queryApiIntrospection, previewState),
    setPreviewState
  );

  function handleClearState(): void {
    setDataFrameNameState(DEFAULT_DATA_FRAME_NAME_STATE);
    setQueryFormState(DEFAULT_QUERY_FORM_STATE);
    setPreviewState(NOT_FETCHED_ASYNC_STATE);
  }

  // Memoized so that RegistrySchemasStepperForm doesn't rerender unnecessarily and cause the QueryApiFilters
  // to rerender and close if already opened.
  const handleChangeQueryFormState = useCallback(
    (newFormState: FormState) => {
      const totalFields =
        newFormState.entityListingFieldName == null
          ? 0
          : countSelectedFields(newFormState.entityFieldSelections.selections) +
            sum(
              newFormState.relatedResultsFormStates.map((resultFormState) =>
                countSelectedFields(resultFormState.backlinkSelections.selections)
              )
            );
      if (totalFields > MAX_NUM_QUERY_API_FIELDS) {
        notifications.error(`Only ${MAX_NUM_QUERY_API_FIELDS} column selections are allowed.`, {
          duration: 4,
        });
      } else {
        setQueryFormState(newFormState);
      }
    },
    [setQueryFormState]
  );

  const onFinalize = async function handleReplaceAndRunStep(): Promise<void> {
    assert(queryAst != null && dataFrameNameState.name !== '', 'Tried to finalize with incomplete form');

    setIsFinalizing(true);
    try {
      const newStep = await AnalysisActions.addOrReplaceAndRunAnalysisStep(
        analysis.id,
        {
          stepType: PipelineStepType.REGISTRY_SCHEMA_DATASET,
          stepVersion: 0,
          stepConfig: {
            dataFrameName: dataFrameNameState.name,
            query: astToString(queryAst),
            queryDescription: await formStateToHumanReadableString(queryFormState, queryApiIntrospection),
          },
        },
        selectedItem.stepIdToReplace
          ? {
              originalStepId: selectedItem.stepIdToReplace,
              columnMapping: selectedColumnsToColumnMappings(selectedColumns, templateDataFrameColumns),
            }
          : undefined
      );
      openSelectedItemForNewStep(newStep, onCloseStepInitializationView);
      handleClearState();
      setIsFinalizing(false);
    } catch (err) {
      setIsFinalizing(false);
      throw err;
    }
  };

  const previewDataFrame = getMostRecentData(previewState)?.dataFrame ?? null;

  if (previewDataFrame?.columns) {
    unselectColumnsIfColumnIsNotInTemplateColumns(
      selectedColumns,
      previewDataFrame.columns,
      setSelectedColumns
    );
  }

  return (
    <StepConfigurationViewTemplate
      {...getSharedDataFrameCreationMethodProps(selectedItem)}
      clientStateId={clientStateId}
      dataFrames={previewDataFrame == null ? [] : [{...previewDataFrame, name: dataFrameNameState.name}]}
      disableFinalizeButton={!canFinalize}
      isFinalizing={isFinalizing}
      isLoadingPreview={previewState.status === AsyncStatus.LOADING}
      messages={buildErrorAndWarningMessages(previewState)}
      noPreviewPlaceholderOptions={{
        title: DATA_FRAME_CREATION_NO_PREVIEW_PLACEHOLDER_TEXT,
        subtitle: 'Select a registry schema to see a preview of your table.',
      }}
      defaultDataFrameTableMode={DataFrameTableMode.TEXT}
      onCancel={() => {
        handleClearState();
        onCloseStepInitializationView();
      }}
      // TODO(BNCH-53649): Explore reusing the finalization logic from CreateDataFrameFromAnalyticsBlockView
      onFinalize={onFinalize}
      title="Registry schemas"
      toolbar={
        <ChooseDifferentDataFrameCreationMethodButton
          onOpenStepInitializationView={(newSelectedItem: {
            type: AnalysisMainViewItemType.CHOOSE_DATASET_CREATION_METHOD;
          }) => {
            handleClearState();

            onOpenStepInitializationView({
              ...newSelectedItem,
              stepIdToReplace: selectedItem.stepIdToReplace,
            });
          }}
        />
      }
    >
      {queryApiIntrospection.status === QueryResultType.ERROR ? (
        <InlineBanner bannerType={InlineBannerType.ERROR} text="Error loading registry schemas." />
      ) : queryApiIntrospection.status === QueryResultType.PENDING ? (
        <CenteredHorizontalThrobber />
      ) : (
        <RegistrySchemaStepperForm
          queryApiIntrospection={queryApiIntrospection}
          queryFormState={queryFormState}
          setQueryFormState={handleChangeQueryFormState}
          setIsManuallyChanged={setIsManuallyChanged}
          dataFrameNameState={dataFrameNameState}
          setDataFrameNameState={setDataFrameNameState}
          columnMappingProps={
            selectedItem.stepIdToReplace != null
              ? {
                  templateDataFrameColumns,
                  newDataFrameColumns: previewDataFrame?.columns ?? null,
                  newDataFrameState: previewState.status,
                  selectedColumns,
                  setSelectedColumns,
                  isManuallyChanged,
                  setIsManuallyChanged,
                }
              : undefined
          }
        />
      )}
    </StepConfigurationViewTemplate>
  );
}
